import { Alert, Button, Collapse, Group, Stack, Text, Title, Tooltip } from '@common/ui'
import { useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { ColumnDef, ColumnSizingState, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import { usePermissionPoliciesInfinite } from '../../io/usePermissionPolicies'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import computeActionsBreakdown, { ActionsBreakdown } from '../../domain/permission/computeActionsBreakdown'
import { StringListCell, TypesPermissionsCell } from './permissionListCells'
import InfiniteScrollTable from '../../components/data/InfiniteScrollTable'
import TablePanel from '../../components/data/TablePanel'
import AccessControlTabs from './AccessControlTabs'
import useHasGlobalPermissions from './useHasGlobalPermissions'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import GlobalAction = com.eidu.entity.permission.GlobalAction

const NameCell = ({ value }: { value: string }) => (
  <Tooltip label={value} position="top-start">
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <Text size="sm" truncate fw={600} lh={1.3}>
        {value}
      </Text>
    </div>
  </Tooltip>
)

type PolicyRow = {
  id: string
  rowNumber: number
  name: string
  actions: ActionsBreakdown
}

type PermissionPoliciesListPageContentProps = {
  policies: ReadonlyArray<PermissionPolicyDTO>
  allEntityTypes: ReadonlyMap<EntityTypeId, EntityType | null> | undefined
  totalCount: number
  hasMore: boolean
  loading: boolean
  loadMore: () => void
  onNewClicked: () => void
  onPolicyClicked: (id: string) => void
}

const PermissionPoliciesListPageContent = ({
  policies,
  allEntityTypes,
  totalCount,
  hasMore,
  loading,
  loadMore,
  onNewClicked,
  onPolicyClicked,
}: PermissionPoliciesListPageContentProps) => {
  const rows: PolicyRow[] = useMemo(
    () =>
      policies.map((policyDocument, index) => ({
        id: policyDocument.policyId.toString(),
        rowNumber: index + 1,
        name: policyDocument.policy.name,
        actions: computeActionsBreakdown(
          [...policyDocument.policy.permissions.asJsReadonlySetView()],
          allEntityTypes ?? new Map()
        ),
      })),
    [policies, allEntityTypes]
  )

  const columns = useMemo<ColumnDef<PolicyRow>[]>(
    () => [
      {
        id: 'row',
        header: () => <span>Row</span>,
        accessorKey: 'rowNumber',
        size: 70,
        minSize: 50,
        enableResizing: false,
      },
      {
        id: 'name',
        header: () => <span>Name</span>,
        cell: ({ row }) => <NameCell value={row.original.name} />,
        size: 300,
        minSize: 150,
      },
      {
        id: 'globalPermissions',
        header: () => <span>Global permissions</span>,
        cell: ({ row }) => <StringListCell value={row.original.actions.global} />,
        size: 300,
        minSize: 150,
      },
      {
        id: 'typesPermissions',
        header: () => <span>Types permissions</span>,
        cell: ({ row }) => <TypesPermissionsCell value={row.original.actions.perType} />,
        size: 500,
        minSize: 200,
      },
    ],
    []
  )

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  return (
    <Stack style={{ flex: 1, minHeight: 0, overflow: 'hidden' }} p="lg" gap="md">
      <TablePanel
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            backgroundColor: 'var(--trellis-color-surface-variant)',
            color: 'var(--trellis-color-on-surface-variant)',
            borderBottom: '1px solid var(--trellis-color-outline)',
            flexShrink: 0,
          }}
        >
          <Stack gap={0}>
            <Title order={4} size="md">
              Permission policies
            </Title>
            <Text size="xs" c="dimmed">
              {totalCount} total
            </Text>
          </Stack>
          <Group gap="xs">
            <Button variant="outline" size="xs" onClick={onNewClicked}>
              New policy
            </Button>
          </Group>
        </Group>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <InfiniteScrollTable
            table={table}
            loading={loading}
            hasMore={hasMore}
            loadMore={loadMore}
            emptyMessage="No permission policies"
            containerTestId="permission-policies-table"
            onRowClick={(row) => onPolicyClicked(row.id)}
            estimateRowSize={56}
          />
        </div>
      </TablePanel>
    </Stack>
  )
}

const PermissionPoliciesListPage = () => {
  const navigate = useNavigate()
  const { entityTypes: allEntityTypes, error: typesError } = useEntityTypes()
  const { policies, totalCount, hasMore, loading, loadMore, error: policiesError } = usePermissionPoliciesInfinite()

  const { hasPermission, loading: permissionsLoading } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)
  const error = typesError ?? policiesError

  if (permissionsLoading || hasManagePermissionGrantsPermission === false) return null

  return (
    <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
      <AccessControlTabs />
      <Collapse expanded={!!error}>
        <Alert
          variant="filled"
          severity="negative"
          m="md"
          action={
            <Button variant="white" size="xs" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <div>{String(error)}</div>
          <div>Please reload the page and try again.</div>
        </Alert>
      </Collapse>
      {!error && (
        <PermissionPoliciesListPageContent
          policies={policies}
          allEntityTypes={allEntityTypes}
          totalCount={totalCount}
          hasMore={hasMore}
          loading={loading}
          loadMore={loadMore}
          onNewClicked={() => navigate('new')}
          onPolicyClicked={(id) => navigate(id)}
        />
      )}
    </Stack>
  )
}

const PermissionPoliciesListPageRoute = () => <PermissionPoliciesListPage />

export { PermissionPoliciesListPage }
export default PermissionPoliciesListPageRoute
